export interface AppConfig {
  cognitoClientId: string;
  cognitoDomain: string;
  apiBaseUrl: string;
  appUrl: string;
  // Identity provider to pin on the Hosted-UI authorize call, so sign-in goes straight to
  // Enterprise Auth instead of the Cognito chooser. Empty string means "show the chooser",
  // which is what sandbox and local dev want — both sign-in paths stay one click away.
  // CDK sets this only where it should be pinned (see `Auth.signInIdentityProvider`).
  signInIdentityProvider: string;
}

// Vite env vars are local-dev fallbacks (populated by `make env`).
// In deployed environments, loadConfig() overwrites these from /config.json,
// which CDK generates with CloudFormation-resolved values at deploy time.
export const config: AppConfig = {
  cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
  cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN ?? '',
  apiBaseUrl:
    import.meta.env.VITE_API_BASE_URL ?? 'https://api.timesheets-sandbox.eta.its.utexas.edu',
  appUrl: import.meta.env.VITE_APP_URL ?? 'http://localhost:5173',
  signInIdentityProvider: import.meta.env.VITE_SIGN_IN_IDENTITY_PROVIDER ?? '',
};

export async function loadConfig(): Promise<void> {
  let res: Response;
  try {
    res = await fetch('/config.json');
  } catch {
    // Local dev without a config.json available — Vite env var defaults remain in effect
    return;
  }
  if (!res.ok) {
    // 404 expected in local dev; other non-OK responses are unexpected but non-fatal
    return;
  }
  // Vite's SPA fallback (local dev) and CloudFront's SPA fallback both return
  // text/html with a 200 when the path doesn't match a real file.  Only parse
  // the response when the server explicitly says it's JSON, so we don't mistake
  // an HTML fallback for a valid config — and still let real parse errors throw.
  if (!res.headers.get('content-type')?.includes('application/json')) {
    return;
  }
  const data = (await res.json()) as Partial<AppConfig>;
  Object.assign(config, data);
}
